import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  Search, RefreshCw, Download, Plus, X, Filter, ListChecks,
  ChevronDown, ChevronRight, ExternalLink, MessageSquare,
  CheckCircle2, AlertTriangle, ArrowUpRight, UserPlus, FileDown,
  Clock, MapPin, ShieldAlert, ShoppingBag, Tag,
  LayoutGrid, Table as TableIcon, Image as ImageIcon, Loader2,
} from 'lucide-react';
import { useBaseUserId } from '../context/UserAuthContext';

// Reusable container class — black background, white-ish outline, dashboard hover glow.
const CONTAINER_CLS =
  'bg-black border border-gray-400 rounded-xl hover:border-gray-300 ' +
  'hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300';

const PLATFORMS = ['Amazon', 'eBay', 'TikTok Shop', 'DHGate', 'Etsy', 'Facebook Marketplace', 'Instagram Shops'];
const STATUS_LABELS = {
  pending_review: 'Pending Review',
  confirmed: 'Confirmed',
  false_positive: 'False Positive',
  escalated: 'Escalated',
  takedown_submitted: 'Takedown Submitted',
  removed: 'Removed',
};
const STATUS_COLOURS = {
  pending_review: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  confirmed: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  false_positive: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  escalated: 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  takedown_submitted: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/30',
  removed: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};
const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const PRIORITY_COLOURS = {
  urgent: 'text-rose-400',
  high: 'text-amber-400',
  medium: 'text-cyan-400',
  low: 'text-gray-400',
};
const AI_BANDS = [
  { label: '90–100%', min: 90, max: 100, key: '90-100' },
  { label: '75–89%', min: 75, max: 89, key: '75-89' },
  { label: '50–74%', min: 50, max: 74, key: '50-74' },
  { label: 'Below 50%', min: 0, max: 49, key: '0-49' },
];
const PRODUCT_CATS = ['Shoes', 'Apparel', 'Electronics', 'Accessories', 'Luxury', 'Beauty'];

const formatRelative = (sec) => {
  if (sec === null || sec === undefined) return '—';
  const abs = Math.abs(sec);
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const str = `${h}h ${m}m`;
  return sec < 0 ? `Overdue ${str}` : `${str} remaining`;
};
const formatDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const CounterfindProductQueue = () => {
  const apiBase = window.location.origin;
  const baseUserId = useBaseUserId();
  const [listings, setListings] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('cf_queue_view') || 'card'; } catch { return 'card'; }
  });
  useEffect(() => { try { localStorage.setItem('cf_queue_view', viewMode); } catch { /* noop */ } }, [viewMode]);

  // Filters
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState(new Set());
  const [brandOptions, setBrandOptions] = useState([]);
  const [statusFilter, setStatusFilter] = useState(new Set());
  const [platformFilter, setPlatformFilter] = useState(new Set());
  const [aiBandFilter, setAiBandFilter] = useState(new Set());
  const [priorityFilter, setPriorityFilter] = useState(new Set());
  const [categoryFilter, setCategoryFilter] = useState(new Set());
  const [countryFilter, setCountryFilter] = useState('');
  const [assigneeFilter, setAssigneeFilter] = useState('all'); // all | unassigned | me | team

  // Selection (bulk actions)
  const [selected, setSelected] = useState(new Set());
  const [openDetailId, setOpenDetailId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const toggle = (setter, set, val) => {
    const next = new Set(set);
    next.has(val) ? next.delete(val) : next.add(val);
    setter(next);
  };

  // -- Fetch --
  const fetchAll = useCallback(async () => {
    if (!baseUserId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ base_user_id: baseUserId, limit: '300' });
      if (search) qs.set('q', search);
      if (statusFilter.size) qs.set('status', [...statusFilter].join(','));
      if (platformFilter.size) qs.set('platform', [...platformFilter].join(','));
      if (brandFilter.size) qs.set('brand', [...brandFilter].join(','));
      if (priorityFilter.size) qs.set('priority', [...priorityFilter].join(','));
      if (categoryFilter.size) qs.set('product_category', [...categoryFilter].join(','));
      if (countryFilter) qs.set('country', countryFilter);
      if (assigneeFilter === 'unassigned') qs.set('assignee', '__unassigned__'); // handled client-side below
      if (aiBandFilter.size) {
        // Server takes min/max — pick the widest band selected
        const bands = [...aiBandFilter].map(k => AI_BANDS.find(b => b.key === k)).filter(Boolean);
        const min = Math.min(...bands.map(b => b.min));
        const max = Math.max(...bands.map(b => b.max));
        qs.set('ai_score_min', String(min));
        qs.set('ai_score_max', String(max));
      }
      const [lRes, mRes] = await Promise.all([
        fetch(`${apiBase}/api/counterfind/queue/listings?${qs.toString()}`).then(r => r.json()),
        fetch(`${apiBase}/api/counterfind/queue/metrics?base_user_id=${encodeURIComponent(baseUserId)}`).then(r => r.json()),
      ]);
      let items = lRes.listings || [];
      if (assigneeFilter === 'unassigned') items = items.filter(i => !i.assignee);
      if (assigneeFilter === 'team') items = items.filter(i => i.assignee && /team/i.test(i.assignee));
      setListings(items);
      setMetrics(mRes);
    } catch (e) {
      console.error('Queue fetch failed', e);
      toast.error('Could not load the queue.');
    } finally {
      setLoading(false);
    }
  }, [baseUserId, apiBase, search, statusFilter, platformFilter, brandFilter, aiBandFilter, priorityFilter, categoryFilter, countryFilter, assigneeFilter]);

  // Load brand list (curated 50+ + any custom brands on this account)
  useEffect(() => {
    if (!baseUserId) return;
    fetch(`${apiBase}/api/counterfind/queue/brands?base_user_id=${encodeURIComponent(baseUserId)}`)
      .then(r => r.json())
      .then(j => setBrandOptions(j.brands || []))
      .catch(() => { /* dropdown stays empty until /brands is reachable */ });
  }, [baseUserId, apiBase]);

  // Seed once + initial fetch
  useEffect(() => {
    if (!baseUserId) return;
    (async () => {
      try { await fetch(`${apiBase}/api/counterfind/queue/seed?base_user_id=${encodeURIComponent(baseUserId)}`, { method: 'POST' }); }
      catch { /* idempotent — already seeded */ }
      fetchAll();
    })();
  }, [baseUserId, apiBase, fetchAll]);

  // Refetch when filters change
  useEffect(() => { fetchAll(); }, [fetchAll]);

  // -- Actions --
  const doAction = async (id, action, extras = {}) => {
    try {
      await fetch(`${apiBase}/api/counterfind/queue/listings/${id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ...extras }),
      });
      toast.success(`${STATUS_LABELS[action] || action.replace('_', ' ')} applied.`);
      fetchAll();
    } catch (e) {
      toast.error('Action failed.');
    }
  };
  const doBulk = async (action, extras = {}) => {
    if (!selected.size) { toast.warning('Select at least one listing.'); return; }
    try {
      const res = await fetch(`${apiBase}/api/counterfind/queue/listings/bulk-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ listing_ids: [...selected], action, ...extras }),
      });
      const j = await res.json();
      toast.success(`${j.updated || 0} updated.`);
      setSelected(new Set());
      fetchAll();
    } catch {
      toast.error('Bulk action failed.');
    }
  };
  const exportCsv = () => {
    const rows = selected.size ? listings.filter(l => selected.has(l.id)) : listings;
    const cols = ['id', 'product_title', 'brand', 'platform', 'seller_name', 'seller_country', 'status', 'priority', 'ai_score', 'product_url', 'created_at'];
    const csv = [cols.join(',')].concat(rows.map(r => cols.map(c => {
      const v = r[c] === null || r[c] === undefined ? '' : String(r[c]).replace(/"/g, '""');
      return /[",\n]/.test(v) ? `"${v}"` : v;
    }).join(','))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `counterfind_queue_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const totalSelected = selected.size;
  const allVisibleIds = useMemo(() => listings.map(l => l.id), [listings]);
  const allSelected = totalSelected > 0 && allVisibleIds.every(id => selected.has(id));

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6" data-testid="counterfind-product-queue">
      <div className="max-w-[1900px] mx-auto space-y-4">
        {/* HEADER + METRICS ROW */}
        <div className="flex items-center gap-3 mb-1">
          <ShieldAlert size={26} className="text-amber-400" />
          <h1 className="text-2xl md:text-3xl font-bold">Product Queue</h1>
          <span className="text-sm text-gray-400">Brand-protection review pipeline</span>
        </div>

        {/* METRICS — 6 small containers */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="queue-metrics-row">
          <Metric label="Found Today" value={metrics.listings_found_today} testId="m-found-today" />
          <Metric label="Pending Reviews" value={metrics.pending_reviews} testId="m-pending" />
          <Metric label="Confirmed" value={metrics.confirmed_counterfeits} testId="m-confirmed" />
          <Metric label="Takedowns" value={metrics.takedowns_submitted} testId="m-takedowns" />
          <Metric label="Removed" value={metrics.removed_listings} testId="m-removed" />
          <Metric label="Escalations" value={metrics.escalations} testId="m-escalations" />
        </div>

        {/* TOP CONTROL BAR */}
        <div className={`${CONTAINER_CLS} p-3 md:p-4`}>
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <BrandDropdown
              brands={brandOptions}
              selected={brandFilter}
              onChange={setBrandFilter}
            />
            <div className="relative flex-1 min-w-0">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                data-testid="queue-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search brand, seller, URL, platform, product, or case ID…"
                className="w-full bg-black border border-gray-700 focus:border-gray-400 rounded-lg pl-9 pr-3 py-2 text-sm placeholder-gray-500 outline-none transition-colors"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={fetchAll} data-testid="queue-refresh" className="flex items-center gap-1.5 px-3 py-2 text-sm bg-black border border-gray-600 hover:border-gray-300 rounded-lg transition-colors">
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
              </button>
              <BulkMenu disabled={!totalSelected} count={totalSelected} doBulk={doBulk} exportCsv={exportCsv} />
              <button onClick={exportCsv} data-testid="queue-export" className="flex items-center gap-1.5 px-3 py-2 text-sm bg-black border border-gray-600 hover:border-gray-300 rounded-lg transition-colors">
                <Download size={14} /> Export
              </button>
              <button onClick={() => setShowCreate(true)} data-testid="queue-create-case" className="flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors">
                <Plus size={14} /> Create Case
              </button>
            </div>
          </div>
        </div>

        {/* SIDEBAR + MAIN */}
        <div className="grid grid-cols-12 gap-4">
          {/* LEFT SIDEBAR FILTERS */}
          <aside className={`col-span-12 lg:col-span-3 xl:col-span-2 ${CONTAINER_CLS} p-4 self-start`} data-testid="queue-filters-sidebar">
            <FilterGroup title="Status">
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <FilterCheck key={k} testId={`filter-status-${k}`} checked={statusFilter.has(k)} onChange={() => toggle(setStatusFilter, statusFilter, k)} label={v} />
              ))}
            </FilterGroup>
            <FilterGroup title="Platforms">
              {PLATFORMS.map(p => (
                <FilterCheck key={p} testId={`filter-platform-${p.replace(/\s+/g, '-').toLowerCase()}`} checked={platformFilter.has(p)} onChange={() => toggle(setPlatformFilter, platformFilter, p)} label={p} />
              ))}
            </FilterGroup>
            <FilterGroup title="AI Confidence">
              {AI_BANDS.map(b => (
                <FilterCheck key={b.key} testId={`filter-ai-${b.key}`} checked={aiBandFilter.has(b.key)} onChange={() => toggle(setAiBandFilter, aiBandFilter, b.key)} label={b.label} />
              ))}
            </FilterGroup>
            <FilterGroup title="Priority">
              {PRIORITIES.map(p => (
                <FilterCheck key={p} testId={`filter-priority-${p}`} checked={priorityFilter.has(p)} onChange={() => toggle(setPriorityFilter, priorityFilter, p)} label={p[0].toUpperCase() + p.slice(1)} />
              ))}
            </FilterGroup>
            <FilterGroup title="Assignment">
              {['all', 'unassigned', 'me', 'team'].map(o => (
                <button key={o}
                  data-testid={`filter-assignee-${o}`}
                  onClick={() => setAssigneeFilter(o)}
                  className={`w-full text-left text-xs px-2 py-1.5 rounded ${assigneeFilter === o ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white'}`}>
                  {o === 'me' ? 'Assigned to Me' : o === 'team' ? 'Assigned Team' : o === 'unassigned' ? 'Unassigned' : 'All'}
                </button>
              ))}
            </FilterGroup>
            <FilterGroup title="Product Categories">
              {PRODUCT_CATS.map(c => (
                <FilterCheck key={c} testId={`filter-cat-${c.toLowerCase()}`} checked={categoryFilter.has(c)} onChange={() => toggle(setCategoryFilter, categoryFilter, c)} label={c} />
              ))}
            </FilterGroup>
            <FilterGroup title="Geography">
              <input
                data-testid="filter-country"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                placeholder="Country (e.g. China)"
                className="w-full bg-black border border-gray-700 focus:border-gray-400 rounded px-2 py-1.5 text-xs placeholder-gray-500 outline-none"
              />
            </FilterGroup>
          </aside>

          {/* MAIN AREA */}
          <main className="col-span-12 lg:col-span-9 xl:col-span-10 space-y-4">
            {/* View mode toggle row + bulk header */}
            <div className={`${CONTAINER_CLS} p-3 flex items-center justify-between flex-wrap gap-3`}>
              <div className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  data-testid="queue-select-all"
                  checked={allSelected}
                  onChange={() => {
                    if (allSelected) setSelected(new Set());
                    else setSelected(new Set(allVisibleIds));
                  }}
                  className="accent-emerald-500"
                />
                <span className="text-gray-400">
                  {totalSelected ? `${totalSelected} selected · ` : ''}
                  {listings.length} listings
                </span>
              </div>
              <div className="inline-flex items-center bg-gray-900 border border-gray-700 rounded-lg p-0.5">
                {[
                  ['table', TableIcon, 'Table'],
                  ['card', LayoutGrid, 'Card'],
                  ['image', ImageIcon, 'Image'],
                ].map(([k, I, label]) => (
                  <button
                    key={k}
                    data-testid={`view-${k}`}
                    onClick={() => setViewMode(k)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === k ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-400 hover:text-white'}`}>
                    <I size={13} /> {label}
                  </button>
                ))}
              </div>
            </div>

            {/* LISTINGS */}
            {loading ? (
              <div className={`${CONTAINER_CLS} p-12 text-center text-gray-400`}>
                <Loader2 className="animate-spin inline mr-2" /> Loading queue…
              </div>
            ) : listings.length === 0 ? (
              <div className={`${CONTAINER_CLS} p-12 text-center text-gray-500`}>
                No listings match your filters.
              </div>
            ) : viewMode === 'table' ? (
              <TableView listings={listings} selected={selected} setSelected={setSelected} onOpen={setOpenDetailId} onAction={doAction} />
            ) : viewMode === 'card' ? (
              <CardView listings={listings} selected={selected} setSelected={setSelected} onOpen={setOpenDetailId} onAction={doAction} />
            ) : (
              <ImageView listings={listings} selected={selected} setSelected={setSelected} onOpen={setOpenDetailId} onAction={doAction} />
            )}
          </main>
        </div>
      </div>

      {/* DETAIL DRAWER */}
      {openDetailId && (
        <DetailDrawer id={openDetailId} onClose={() => setOpenDetailId(null)} onActionDone={fetchAll} />
      )}

      {/* CREATE MODAL */}
      {showCreate && (
        <CreateCaseModal baseUserId={baseUserId} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchAll(); }} />
      )}
    </div>
  );
};

// ----- Sub-components -----

const Metric = ({ label, value, testId }) => (
  <div className={`${CONTAINER_CLS} p-4`} data-testid={testId}>
    <div className="text-2xl font-bold tabular-nums">{value ?? 0}</div>
    <div className="text-xs text-gray-400 mt-1">{label}</div>
  </div>
);

const FilterGroup = ({ title, children }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="mb-3">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-gray-400 mb-1.5">
        <span>{title}</span>
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
      </button>
      {open && <div className="space-y-1">{children}</div>}
    </div>
  );
};
const FilterCheck = ({ label, checked, onChange, testId }) => (
  <label data-testid={testId} className="flex items-center gap-2 text-xs text-gray-300 hover:text-white cursor-pointer py-0.5">
    <input type="checkbox" checked={checked} onChange={onChange} className="accent-emerald-500 w-3.5 h-3.5" />
    <span>{label}</span>
  </label>
);

const AiBadge = ({ score }) => {
  const colour =
    score >= 90 ? 'bg-rose-500/20 text-rose-300 border-rose-500/40' :
    score >= 75 ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
    score >= 50 ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40' :
                  'bg-gray-500/20 text-gray-300 border-gray-500/40';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <span className={`px-2 py-0.5 rounded text-[11px] font-bold border ${colour}`}>{score}%</span>
      <div className="flex-1 h-1.5 bg-gray-900 rounded-full overflow-hidden min-w-[40px]">
        <div className={`h-full ${score >= 90 ? 'bg-rose-500' : score >= 75 ? 'bg-amber-500' : score >= 50 ? 'bg-cyan-500' : 'bg-gray-500'}`} style={{ width: `${score}%` }} />
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wide font-semibold border ${STATUS_COLOURS[status] || ''}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const SlaTimer = ({ seconds }) => {
  if (seconds === null || seconds === undefined) return null;
  const over = seconds < 0;
  return (
    <span className={`flex items-center gap-1 text-[11px] ${over ? 'text-rose-400' : 'text-gray-300'}`}>
      <Clock size={11} /> {formatRelative(seconds)}
    </span>
  );
};

const QuickActions = ({ listing, onAction, onOpen, size = 'sm' }) => {
  const cls = size === 'sm' ? 'p-1.5' : 'p-2';
  return (
    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => onAction(listing.id, 'confirm')} title="Confirm counterfeit" data-testid={`act-confirm-${listing.id}`} className={`${cls} rounded hover:bg-rose-500/20 text-rose-400`}>
        <CheckCircle2 size={15} />
      </button>
      <button onClick={() => onAction(listing.id, 'false_positive')} title="False positive" data-testid={`act-fp-${listing.id}`} className={`${cls} rounded hover:bg-gray-500/20 text-gray-300`}>
        <X size={15} />
      </button>
      <button onClick={() => onAction(listing.id, 'escalate')} title="Escalate" data-testid={`act-escalate-${listing.id}`} className={`${cls} rounded hover:bg-purple-500/20 text-purple-300`}>
        <ArrowUpRight size={15} />
      </button>
      <button onClick={() => onAction(listing.id, 'takedown')} title="Submit takedown" data-testid={`act-takedown-${listing.id}`} className={`${cls} rounded hover:bg-cyan-500/20 text-cyan-300`}>
        <ShieldAlert size={15} />
      </button>
      <button onClick={() => onOpen(listing.id)} title="Open details" data-testid={`act-open-${listing.id}`} className={`${cls} rounded hover:bg-gray-700 text-white`}>
        <ExternalLink size={15} />
      </button>
    </div>
  );
};

// ----- VIEW: Table -----
const TableView = ({ listings, selected, setSelected, onOpen, onAction }) => (
  <div className={`${CONTAINER_CLS} overflow-auto`} data-testid="queue-table-view">
    <table className="w-full text-sm">
      <thead className="text-xs text-gray-400 uppercase border-b border-gray-700">
        <tr>
          <th className="p-3 w-8"></th>
          <th className="p-3 text-left">Listing</th>
          <th className="p-3 text-left">Platform</th>
          <th className="p-3 text-left">AI Score</th>
          <th className="p-3 text-left">Status</th>
          <th className="p-3 text-left">Assignee</th>
          <th className="p-3 text-left">SLA</th>
          <th className="p-3 text-right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {listings.map(l => {
          const isSel = selected.has(l.id);
          return (
            <tr key={l.id}
              data-testid={`row-${l.id}`}
              onClick={() => onOpen(l.id)}
              className={`border-b border-gray-800 hover:bg-gray-900/50 cursor-pointer ${isSel ? 'bg-gray-900/40' : ''}`}>
              <td className="p-3" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={isSel}
                  onChange={() => {
                    const next = new Set(selected);
                    next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                    setSelected(next);
                  }}
                  className="accent-emerald-500"
                />
              </td>
              <td className="p-3">
                <div className="flex items-center gap-3 min-w-0">
                  <img src={l.image_urls?.[0]} alt="" className="w-10 h-10 rounded object-cover bg-gray-900 flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />
                  <div className="min-w-0">
                    <div className="text-white truncate" title={l.product_title}>{l.product_title}</div>
                    <div className="text-xs text-gray-400 flex items-center gap-2">
                      <span>{l.brand}</span> · <span className="truncate">{l.seller_name}</span>
                    </div>
                  </div>
                </div>
              </td>
              <td className="p-3 text-gray-300">{l.platform}</td>
              <td className="p-3 w-44"><AiBadge score={l.ai_score} /></td>
              <td className="p-3"><StatusBadge status={l.status} /></td>
              <td className="p-3 text-gray-300">{l.assignee || <span className="text-gray-500 italic">Unassigned</span>}</td>
              <td className="p-3"><SlaTimer seconds={l.sla_remaining_seconds} /></td>
              <td className="p-3 text-right">
                <QuickActions listing={l} onAction={onAction} onOpen={onOpen} />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// ----- VIEW: Card -----
const CardView = ({ listings, selected, setSelected, onOpen, onAction }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="queue-card-view">
    {listings.map(l => {
      const isSel = selected.has(l.id);
      return (
        <div key={l.id}
          data-testid={`card-${l.id}`}
          onClick={() => onOpen(l.id)}
          className={`${CONTAINER_CLS} p-3 cursor-pointer ${isSel ? 'ring-2 ring-emerald-500/60' : ''}`}>
          <div className="flex items-start gap-3 mb-2">
            <div className="relative flex-shrink-0">
              <img src={l.image_urls?.[0]} alt="" className="w-16 h-16 rounded-lg object-cover bg-gray-900" onError={(e) => { e.target.style.display = 'none'; }} />
              <input
                type="checkbox"
                checked={isSel}
                onClick={(e) => e.stopPropagation()}
                onChange={() => {
                  const next = new Set(selected);
                  next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                  setSelected(next);
                }}
                className="absolute -top-1 -left-1 accent-emerald-500"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div className="text-white text-sm font-medium truncate" title={l.product_title}>{l.product_title}</div>
                <StatusBadge status={l.status} />
              </div>
              <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
                <ShoppingBag size={11} /> {l.brand} · {l.platform}
              </div>
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1.5">
                <MapPin size={11} /> {l.seller_name} · {l.seller_country}
              </div>
            </div>
          </div>
          <div className="mb-2"><AiBadge score={l.ai_score} /></div>
          <div className="text-[11px] text-gray-400 flex flex-wrap gap-1 mb-2">
            {(l.ai_reasoning || []).slice(0, 3).map((r, i) => (
              <span key={i} className="px-1.5 py-0.5 bg-gray-900 border border-gray-700 rounded">{r}</span>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-gray-800 pt-2">
            <div className="text-[11px] text-gray-400 flex items-center gap-2">
              <span className={PRIORITY_COLOURS[l.priority]}>● {l.priority}</span>
              <SlaTimer seconds={l.sla_remaining_seconds} />
            </div>
            <QuickActions listing={l} onAction={onAction} onOpen={onOpen} />
          </div>
        </div>
      );
    })}
  </div>
);

// ----- VIEW: Image -----
const ImageView = ({ listings, selected, setSelected, onOpen, onAction }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3" data-testid="queue-image-view">
    {listings.map(l => {
      const isSel = selected.has(l.id);
      return (
        <div key={l.id}
          data-testid={`image-card-${l.id}`}
          onClick={() => onOpen(l.id)}
          className={`${CONTAINER_CLS} overflow-hidden cursor-pointer ${isSel ? 'ring-2 ring-emerald-500/60' : ''}`}>
          <div className="relative aspect-square bg-gray-900">
            <img src={l.image_urls?.[0]} alt="" className="absolute inset-0 w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            <input
              type="checkbox"
              checked={isSel}
              onClick={(e) => e.stopPropagation()}
              onChange={() => {
                const next = new Set(selected);
                next.has(l.id) ? next.delete(l.id) : next.add(l.id);
                setSelected(next);
              }}
              className="absolute top-2 left-2 accent-emerald-500 w-4 h-4"
            />
            <div className="absolute top-2 right-2"><StatusBadge status={l.status} /></div>
            <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black via-black/80 to-transparent">
              <div className="text-white text-sm font-medium truncate">{l.product_title}</div>
              <AiBadge score={l.ai_score} />
            </div>
          </div>
          <div className="p-2 flex items-center justify-between">
            <span className="text-xs text-gray-400 truncate">{l.platform}</span>
            <QuickActions listing={l} onAction={onAction} onOpen={onOpen} />
          </div>
        </div>
      );
    })}
  </div>
);

// ----- Brand Dropdown (left of search bar) -----
const BrandDropdown = ({ brands, selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (b) => {
    const next = new Set(selected);
    next.has(b) ? next.delete(b) : next.add(b);
    onChange(next);
  };
  const clear = () => onChange(new Set());

  const filtered = filter.trim()
    ? brands.filter(b => b.toLowerCase().includes(filter.toLowerCase()))
    : brands;

  const label = selected.size === 0
    ? 'All Brands'
    : selected.size === 1
      ? [...selected][0]
      : `${selected.size} brands`;

  return (
    <div className="relative w-full lg:w-56 shrink-0" ref={ref}>
      <button
        type="button"
        data-testid="queue-brand-dropdown"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm bg-black border border-gray-700 hover:border-gray-400 rounded-lg transition-colors"
      >
        <span className="flex items-center gap-2 min-w-0">
          <Tag size={14} className="text-gray-400 shrink-0" />
          <span className="truncate text-white">{label}</span>
        </span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div
          data-testid="queue-brand-dropdown-panel"
          className="absolute z-40 mt-1 left-0 w-72 max-w-[90vw] bg-black border border-gray-600 rounded-lg shadow-2xl"
        >
          <div className="p-2 border-b border-gray-800 flex items-center gap-2">
            <Search size={13} className="text-gray-500" />
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={`Filter ${brands.length} brands…`}
              data-testid="queue-brand-filter-input"
              className="flex-1 bg-transparent outline-none text-xs placeholder-gray-500 text-white"
            />
            {selected.size > 0 && (
              <button
                onClick={clear}
                data-testid="queue-brand-clear"
                className="text-[10px] uppercase tracking-wider text-rose-400 hover:text-rose-300"
              >
                Clear
              </button>
            )}
          </div>
          <div className="max-h-72 overflow-y-auto custom-scrollbar py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-500 italic">No brands match.</div>
            ) : (
              filtered.map(b => (
                <label
                  key={b}
                  data-testid={`queue-brand-option-${b.replace(/\s+/g, '-').toLowerCase()}`}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-gray-900 cursor-pointer text-white"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(b)}
                    onChange={() => toggle(b)}
                    className="accent-emerald-500"
                  />
                  <span className="truncate">{b}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ----- Bulk Menu -----
const BulkMenu = ({ disabled, count, doBulk, exportCsv }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="queue-bulk-menu"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${disabled ? 'bg-black border-gray-800 text-gray-600 cursor-not-allowed' : 'bg-black border-gray-600 hover:border-gray-300 text-white'}`}>
        <ListChecks size={14} /> Bulk Actions {count ? `(${count})` : ''}
      </button>
      {open && !disabled && (
        <div className="absolute right-0 mt-1 w-48 bg-black border border-gray-600 rounded-lg shadow-xl z-30 py-1">
          {[
            ['confirm', 'Confirm Counterfeit', CheckCircle2],
            ['false_positive', 'Mark False Positive', X],
            ['escalate', 'Escalate', ArrowUpRight],
            ['takedown', 'Submit Takedown', ShieldAlert],
          ].map(([a, label, I]) => (
            <button key={a} data-testid={`bulk-${a}`} onClick={() => { setOpen(false); doBulk(a); }} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-900 flex items-center gap-2">
              <I size={13} /> {label}
            </button>
          ))}
          <div className="border-t border-gray-800 my-1" />
          <button onClick={() => { setOpen(false); exportCsv(); }} data-testid="bulk-export" className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-900 flex items-center gap-2">
            <FileDown size={13} /> Export Selected
          </button>
        </div>
      )}
    </div>
  );
};

// ----- Detail Drawer -----
const DetailDrawer = ({ id, onClose, onActionDone }) => {
  const apiBase = window.location.origin;
  const [doc, setDoc] = useState(null);
  const [note, setNote] = useState('');
  const fetchDoc = useCallback(async () => {
    try {
      const j = await fetch(`${apiBase}/api/counterfind/queue/listings/${id}`).then(r => r.json());
      setDoc(j);
    } catch { setDoc(null); }
  }, [apiBase, id]);
  useEffect(() => { fetchDoc(); }, [fetchDoc]);

  const post = async (action, extras = {}) => {
    await fetch(`${apiBase}/api/counterfind/queue/listings/${id}/action`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, ...extras }),
    });
    await fetchDoc();
    onActionDone && onActionDone();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-2xl bg-black border-l border-gray-400 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="queue-detail-drawer">
        <div className="sticky top-0 z-10 bg-black border-b border-gray-700 p-4 flex items-center justify-between">
          <div>
            <div className="text-xs text-gray-400">Case</div>
            <div className="text-white font-mono text-xs">{id}</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded" data-testid="queue-detail-close"><X size={18} /></button>
        </div>

        {!doc ? (
          <div className="p-8 text-center text-gray-400">Loading…</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Image gallery + title */}
            <div className={`${CONTAINER_CLS} p-3`}>
              <img src={doc.image_urls?.[0]} alt="" className="w-full rounded-lg object-cover max-h-72" onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="mt-3">
                <div className="text-white font-semibold">{doc.product_title}</div>
                <a href={doc.product_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline inline-flex items-center gap-1">
                  <ExternalLink size={11} /> {doc.product_url}
                </a>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <Pair label="Brand" value={doc.brand} />
                <Pair label="Platform" value={doc.platform} />
                <Pair label="Seller" value={`${doc.seller_name} (${doc.seller_country})`} />
                <Pair label="Category" value={doc.product_category} />
                <Pair label="Priority" value={<span className={PRIORITY_COLOURS[doc.priority]}>{doc.priority}</span>} />
                <Pair label="Status" value={<StatusBadge status={doc.status} />} />
                <Pair label="Assignee" value={doc.assignee || 'Unassigned'} />
                <Pair label="Found" value={formatDate(doc.created_at)} />
              </div>
            </div>

            {/* AI analysis */}
            <div className={`${CONTAINER_CLS} p-3`} data-testid="detail-ai-analysis">
              <div className="text-xs uppercase text-gray-400 mb-2">AI Analysis</div>
              <AiBadge score={doc.ai_score} />
              <div className="mt-3 text-xs space-y-1.5">
                {(doc.ai_reasoning || []).map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-300">
                    <AlertTriangle size={12} className="text-amber-400" /> {r}
                  </div>
                ))}
              </div>
              {doc.match_image_similarity_pct !== null && (
                <div className="mt-3 text-xs text-gray-400">Image similarity vs reference: <span className="text-white">{doc.match_image_similarity_pct}%</span></div>
              )}
            </div>

            {/* Seller intelligence */}
            <div className={`${CONTAINER_CLS} p-3`}>
              <div className="text-xs uppercase text-gray-400 mb-2">Seller Intelligence</div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <Stat label="Risk Score" value={`${doc.seller_risk_score}%`} />
                <Stat label="Prior Cases" value={doc.seller_history_count} />
                <Stat label="Country" value={doc.seller_country} />
              </div>
              <div className="mt-2 text-xs text-gray-400">
                Platform takedown success rate: <span className="text-white">{doc.platform_takedown_success_pct}%</span>
              </div>
            </div>

            {/* Timeline */}
            <div className={`${CONTAINER_CLS} p-3`} data-testid="detail-timeline">
              <div className="text-xs uppercase text-gray-400 mb-2">Activity Timeline</div>
              <div className="space-y-2 text-xs">
                {(doc.timeline || []).slice().reverse().map((t, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-1.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="text-white">{t.event.replace(/_/g, ' ')} <span className="text-gray-500">· {t.actor}</span></div>
                      <div className="text-gray-500">{t.detail}</div>
                      <div className="text-gray-600 text-[10px]">{formatDate(t.ts)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className={`${CONTAINER_CLS} p-3`}>
              <div className="text-xs uppercase text-gray-400 mb-2">Internal Notes</div>
              {(doc.notes || []).length === 0 && <div className="text-xs text-gray-500 italic">No notes yet.</div>}
              {(doc.notes || []).slice().reverse().map((n, i) => (
                <div key={i} className="mb-2 border-b border-gray-800 pb-2 last:border-0">
                  <div className="text-xs text-white">{n.text}</div>
                  <div className="text-[10px] text-gray-500">{n.actor} · {formatDate(n.ts)}</div>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input
                  data-testid="detail-note-input"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add internal note…"
                  className="flex-1 bg-black border border-gray-700 rounded px-2 py-1 text-xs outline-none"
                />
                <button onClick={async () => { if (!note.trim()) return; await post('note', { note: note.trim() }); setNote(''); }} className="px-3 py-1 bg-gray-800 hover:bg-gray-700 text-xs rounded">
                  <MessageSquare size={12} className="inline mr-1" /> Add
                </button>
              </div>
            </div>

            {/* Action footer */}
            <div className="sticky bottom-0 bg-black border-t border-gray-700 -mx-4 px-4 py-3 grid grid-cols-2 gap-2">
              <button onClick={() => post('confirm')} className="px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs rounded font-semibold flex items-center justify-center gap-1">
                <CheckCircle2 size={13} /> Confirm Counterfeit
              </button>
              <button onClick={() => post('takedown')} className="px-3 py-2 bg-cyan-600 hover:bg-cyan-500 text-white text-xs rounded font-semibold flex items-center justify-center gap-1">
                <ShieldAlert size={13} /> Submit Takedown
              </button>
              <button onClick={() => post('false_positive')} className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-xs rounded flex items-center justify-center gap-1">
                <X size={13} /> False Positive
              </button>
              <button onClick={() => post('escalate')} className="px-3 py-2 bg-purple-700 hover:bg-purple-600 text-white text-xs rounded flex items-center justify-center gap-1">
                <ArrowUpRight size={13} /> Escalate
              </button>
              <button
                onClick={async () => {
                  const who = prompt('Assign to (name)?', doc.assignee || '');
                  if (who && who.trim()) await post('assign', { assignee: who.trim() });
                }}
                className="px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white text-xs rounded flex items-center justify-center gap-1 col-span-2">
                <UserPlus size={13} /> Assign
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Pair = ({ label, value }) => (
  <div>
    <div className="text-gray-500">{label}</div>
    <div className="text-white truncate">{value}</div>
  </div>
);
const Stat = ({ label, value }) => (
  <div>
    <div className="text-lg font-semibold text-white tabular-nums">{value}</div>
    <div className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</div>
  </div>
);

// ----- Create Case Modal -----
const CreateCaseModal = ({ baseUserId, onClose, onCreated }) => {
  const apiBase = window.location.origin;
  const [form, setForm] = useState({
    product_title: '', product_url: '', brand: '', platform: '',
    seller_name: '', seller_country: '', product_category: 'Other',
    priority: 'medium', notes: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.product_title.trim() || !form.product_url.trim()) {
      toast.error('Title and URL are required.'); return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/counterfind/queue/listings`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ base_user_id: baseUserId, ...form }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('Case created.');
      onCreated();
    } catch (err) { toast.error(`Failed: ${err.message}`); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className={`${CONTAINER_CLS} p-5 w-full max-w-lg space-y-3`}
        data-testid="queue-create-modal">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Create New Case</h3>
          <button type="button" onClick={onClose} className="p-1 hover:bg-gray-800 rounded"><X size={18} /></button>
        </div>
        <Field label="Product Title *">
          <input data-testid="create-title" value={form.product_title} onChange={set('product_title')} className="modal-input" required />
        </Field>
        <Field label="Product URL *">
          <input data-testid="create-url" value={form.product_url} onChange={set('product_url')} type="url" className="modal-input" required />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Brand"><input value={form.brand} onChange={set('brand')} className="modal-input" /></Field>
          <Field label="Platform">
            <select value={form.platform} onChange={set('platform')} className="modal-input">
              <option value="">Auto-detect</option>
              {PLATFORMS.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Seller"><input value={form.seller_name} onChange={set('seller_name')} className="modal-input" /></Field>
          <Field label="Country"><input value={form.seller_country} onChange={set('seller_country')} className="modal-input" /></Field>
          <Field label="Category">
            <select value={form.product_category} onChange={set('product_category')} className="modal-input">
              {['Other', ...PRODUCT_CATS].map(c => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Priority">
            <select value={form.priority} onChange={set('priority')} className="modal-input">
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Notes">
          <textarea value={form.notes} onChange={set('notes')} rows={3} className="modal-input resize-none" />
        </Field>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm border border-gray-600 hover:border-gray-300 rounded">Cancel</button>
          <button type="submit" disabled={busy} data-testid="create-submit" className="px-3 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded disabled:opacity-50">
            {busy ? 'Saving…' : 'Create Case'}
          </button>
        </div>
        <style>{`.modal-input { width:100%; background:black; border:1px solid #4b5563; border-radius:6px; padding:6px 8px; font-size:12px; color:white; outline:none; } .modal-input:focus { border-color:#9ca3af; }`}</style>
      </form>
    </div>
  );
};
const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</span>
    {children}
  </label>
);

export default CounterfindProductQueue;
